import type { CaregiverStatus } from '../../../types';
import type { ColumnDef } from '../../../components/ColumnPicker';

export const STATUS_TONE: Record<CaregiverStatus, string> = {
  'Aktiv': 'success', 'Verfügbar': 'brand', 'Inaktiv': 'neutral',
};

export const AVATAR_TONE: Record<CaregiverStatus, 'brand' | 'green' | 'amber'> = {
  'Aktiv': 'brand', 'Verfügbar': 'green', 'Inaktiv': 'amber',
};

// i18n-Key je Status-Wert (Werte bleiben kanonisch deutsch = Filter-/Datenwerte).
export const STATUS_LABEL_KEY: Record<string, string> = {
  'Aktiv': 'betreuung.list.status.active',
  'Verfügbar': 'betreuung.list.status.available',
  'Inaktiv': 'betreuung.list.status.inactive',
};

// Filter-Werte (kanonisch deutsch) + i18n-Key fürs Label.
export const FILTERS: { value: string; labelKey: string }[] = [
  { value: 'Alle',      labelKey: 'betreuung.list.status.all' },
  { value: 'Aktiv',     labelKey: 'betreuung.list.status.active' },
  { value: 'Verfügbar', labelKey: 'betreuung.list.status.available' },
  { value: 'Inaktiv',   labelKey: 'betreuung.list.status.inactive' },
];

// Arbeitsvertrag (prices_bk.vertrag) — Labels wie in der alten Admin-Liste
// (scripts/Functions.js: bkContracts). Werte bleiben kanonisch (Filterwerte);
// i18n-Key je Vertragsart fürs angezeigte Label.
export const CONTRACT_LABEL_KEY: Record<string, string> = {
  avp: 'betreuung.list.vertrag.avp',
  avd: 'betreuung.list.vertrag.avd',
  avk: 'betreuung.list.vertrag.avk',
  ep:  'betreuung.list.vertrag.ep',
};

// Arbeits-Status (users.status) — Labels/Reihenfolge wie altes UserStatus.js.
export const ARBEITSSTATUS_LABEL_KEY: Record<number, string> = {
  0: 'betreuung.list.arbeitsstatus.verfuegbar',
  1: 'betreuung.list.arbeitsstatus.in_betreuung',
  2: 'betreuung.list.arbeitsstatus.pausierend',
  3: 'betreuung.list.arbeitsstatus.gekuendigt',
  // Manuell gesetzt, vom CareStateWorker nicht überschrieben, nicht im Matching.
  4: 'betreuung.list.arbeitsstatus.nicht_erreichbar',
};
export const ARBEITSSTATUS_TONE: Record<number, string> = {
  0: 'brand', 1: 'success', 2: 'warning', 3: 'danger', 4: 'neutral',
};

export const BETREUUNG_COLUMNS: (ColumnDef & { labelKey: string })[] = [
  { id: 'id',            label: 'ID',              labelKey: 'betreuung.list.header.id' },
  { id: 'name',          label: 'Betreuungskraft', labelKey: 'betreuung.list.header.name', required: true },
  { id: 'role',          label: 'Rolle',           labelKey: 'betreuung.list.header.role' },
  { id: 'location',      label: 'Standort',        labelKey: 'betreuung.list.header.location' },
  { id: 'phone',         label: 'Telefon',         labelKey: 'betreuung.list.header.phone' },
  { id: 'email',         label: 'E-Mail',          labelKey: 'betreuung.list.header.email' },
  { id: 'status',        label: 'Status',          labelKey: 'betreuung.list.header.status' },
  // Alte Admin-BK-Liste (UserList.js): Vertrag direkt neben dem Status.
  { id: 'vertrag',       label: 'Vertrag',         labelKey: 'betreuung.list.header.vertrag' },
  { id: 'arbeitsstatus', label: 'Arbeitsstatus',   labelKey: 'betreuung.list.header.arbeitsstatus' },
  { id: 'invisible',     label: 'Sichtbarkeit',    labelKey: 'betreuung.list.header.invisible' },
  { id: 'pflegegrad',    label: 'Pflegegrad',      labelKey: 'betreuung.list.header.pflegegrad' },
  { id: 'land',          label: 'Land',            labelKey: 'betreuung.list.header.land' },
  { id: 'verfuegbar',    label: 'Verfügbar',       labelKey: 'betreuung.list.header.verfuegbar' },
  // Dispo: laufender bzw. nächster Verfügbarkeits-Eintrag (Verfügbarkeit / Urlaub).
  { id: 'dispo',         label: 'Verfügbarkeit (Dispo)', labelKey: 'betreuung.list.header.dispo' },
  { id: 'origin',        label: 'Ursprung',        labelKey: 'betreuung.list.header.origin' },
  { id: 'assigned',      label: 'Recruiter',       labelKey: 'betreuung.list.header.assigned' },
  { id: 'since',         label: 'Seit',            labelKey: 'betreuung.list.header.since' },
];
export const BETREUUNG_DEFAULT_COLS = ['id', 'name', 'role', 'location', 'phone', 'status', 'vertrag', 'dispo', 'since'];

// Server-seitige Pagination: die v1-Liste hat mehrere Tausend BK. Pro Seite
// laden, "Mehr laden" hängt die nächste Seite an.
export const BETREUUNG_PER_PAGE = 50;

// Spalten-ID → serverseitiges Sortierfeld. `profile` = Spalte auf der jointen
// profiles-Tabelle. Abgeleitete Spalten (role, phone) sind nicht sortierbar.
export const BETREUUNG_SORT_FIELDS: Record<string, { field: string; profile?: boolean }> = {
  id:       { field: 'id' },
  name:     { field: 'nachname', profile: true },
  location: { field: 'ort', profile: true },
  email:    { field: 'email' },
  status:   { field: 'konto' },
  since:    { field: 'created_at' },
  // Spalten der alten Admin-BK-Liste — Sortierfelder wie in getUsersByRole
  // unterstützt (currentContract-Join, derived assigned_admin_name, users.*).
  vertrag:       { field: 'currentContract' },
  arbeitsstatus: { field: 'status' },
  invisible:     { field: 'invisible' },
  pflegegrad:    { field: 'pflegegrad', profile: true },
  land:          { field: 'land' },
  verfuegbar:    { field: 'startdatum' },
  origin:        { field: 'origin' },
  assigned:      { field: 'assigned_admin_name' },
};

/** A7: localStorage-Key und Default-Höhe (px) der Tabellen-Box in /betreuungen. */
export const BETREUUNG_BOX_HEIGHT_KEY = 'sh24.betreuung.boxHeight';
export const BETREUUNG_BOX_DEFAULT_HEIGHT = 760;
